class PopupLibrary {
    constructor(popupCampaign) {
        if (!popupCampaign || !popupCampaign.id) {
            throw new Error('Invalid popup campaign data');
        }
        this.id = popupCampaign.id;
        this.content = popupCampaign.content?.html || '';
        const defaultContentWidth = 500;
        const designContentWidth =
            popupCampaign.content?.design?.body?.values?.contentWidth;
        this.contentWidth = designContentWidth?.includes('px')
            ? parseInt(designContentWidth, 10) || defaultContentWidth
            : defaultContentWidth;
        const {
            timing = { delay: 2, frequency: 'once' },
            position = 'center',
            animation = 'fade',
            targeting = {
                pages: ['*'],
                devices: ['desktop', 'mobile'],
                audience: 'all',
                excludedUrls: [],
            },
            displayTrigger = 'time', stopConditions = {
                afterClose: true,
                afterDisplayCount: 0,
                enableDisplayLimit: false,
                enableStopConditions: true,
            },
        } = popupCampaign.configuration || {};
        this.timing = timing;
        this.position = position;
        this.animation = animation;
        this.targeting = targeting;
        this.displayTrigger = displayTrigger;
        this.stopConditions = stopConditions;

        this.updatedAt = popupCampaign.updated_at || Date.now().toString();

        // DOM elements
        this.popupWrapper = null;
        this.shadowRoot = null;
        this.popupContainer = null;
        this.overlay = null;
        this.closeBtn = null;

        this.storageKeyBase = `popup_${this.id}`;
        this.popupKey = `${this.storageKeyBase}_${this.updatedAt}_shown`;
        this.displayCountKey = `${this.storageKeyBase}_${this.updatedAt}_count`;

        this.isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
        this.deviceType = this.isMobile ? 'mobile' : 'desktop';

        // Event handlers (stored for cleanup)
        this.escapeKeyHandler = this.handleEscapeKey.bind(this);

        this.clearOldStorageKeys();
    }

    clearOldStorageKeys() {
        try {
            const storages = [localStorage, sessionStorage];

            storages.forEach(storage => {
                const keysToRemove = [];

                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (!key) continue;

                    if (key.startsWith('popup_') && (key.endsWith('_shown') || key.endsWith('_count'))) {
                        if (!key.includes('T') && !key.includes('Z') && !key.includes('-')) {
                            keysToRemove.push(key);
                            continue;
                        }
                    }

                    if (key.startsWith(this.storageKeyBase)) {
                        if ((key.endsWith('_shown') || key.endsWith('_count')) && !key.includes(this.updatedAt)) {
                            keysToRemove.push(key);
                        }
                    }
                }

                keysToRemove.forEach(key => storage.removeItem(key));
            });
        } catch (error) {
            // 
        }
    }

    // Initialize and display popup if conditions are met
    init() {
        try {
            // Validate targeting
            if (!this.validateTargeting()) {
                return false;
            }

            const { frequency } = this.timing;

            if (frequency === 'once' && sessionStorage.getItem(this.popupKey)) {
                return false;
            }

            if (frequency === 'oncePerDay') {
                const lastShown = localStorage.getItem(this.popupKey);
                if (lastShown) {
                    const today = new Date().toDateString();
                    if (lastShown === today) {
                        return false;
                    }
                }
            }

            if (this.stopConditions.enableStopConditions !== false) {
                if (
                    this.stopConditions.enableDisplayLimit &&
                    this.stopConditions.afterDisplayCount > 0
                ) {
                    const displayCount = parseInt(
                        localStorage.getItem(this.displayCountKey) || '0',
                        10
                    );
                    if (displayCount >= this.stopConditions.afterDisplayCount) {
                        return false;
                    }
                }

                if (this.stopConditions.afterClose && localStorage.getItem(this.popupKey)) {
                    return false;
                }
            }

            // Ensure we have content
            if (!this.content) {
                // Silently fail if no content
                return false;
            }

            // Create popup elements
            this.createPopupElements();

            // Show popup based on trigger
            this.handleDisplayTrigger();
            return true;
        } catch (error) {
            // Silent fail on errors
            return false;
        }
    }

    // True if the given path is a landing page (prefix /lp/ or exact /lp)
    static isLandingPagePath(pathname) {
        const invalid = !pathname || typeof pathname !== 'string';
        if (invalid) {
            return false;
        }
        const path = pathname.replace(/\/+$/, '') || '/';
        return path === '/lp' || path.startsWith('/lp/');
    }

    // Resolve the "page" URL when widget can run in top window or inside an iframe (embedding page = referrer)
    getPagePathnameForTargeting() {
        const path = window.location.pathname;
        const inIframe = window.self !== window.top;
        try {
            if (inIframe && document.referrer) {
                const refUrl = new URL(document.referrer);
                const refPath = refUrl.pathname || '/';
                if (refUrl.hostname && refPath) {
                    return refPath;
                }
            }
        } catch (e) {
            // Fall through to path param or window pathname
        }
        // When in iframe, parent passes embedding page path as path= in iframe URL (e.g. path=%2Flp%2Fbackpain%2F)
        if (inIframe && typeof window.location.search === 'string') {
            const pathMatch = window.location.search.match(/[?&]path=([^&]+)/);
            if (pathMatch) {
                try {
                    const paramPath = decodeURIComponent(pathMatch[1].replace(/\+/g, ' '));
                    if (paramPath && paramPath.startsWith('/')) {
                        return paramPath;
                    }
                } catch (e2) {
                    // Fall through
                }
            }
        }
        return path;
    }

    // Validate device and page targeting
    validateTargeting() {
        // Never show popup on landing pages (URL path prefix /lp/)
        const pagePath = this.getPagePathnameForTargeting();
        const isLanding = PopupLibrary.isLandingPagePath(pagePath);
        if (isLanding) {
            return false;
        }

        // Device check
        if (!this.targeting.devices.includes(this.deviceType)) {
            return false;
        }

        // Page check (use same resolved path so iframe embedding page is used when applicable)
        const currentPath = pagePath;
        const currentUrl = pagePath;

        if (
            this.targeting.excludedUrls &&
            this.targeting.excludedUrls.length > 0
        ) {
            if (
                this.targeting.excludedUrls.some((excludedUrl) => {
                    if (excludedUrl === '/') {
                        const isExactRootPath =
                            currentUrl === '/' || currentUrl === '';
                        return isExactRootPath;
                    }

                    const isExactMatch =
                        currentUrl === excludedUrl ||
                        currentUrl === `${excludedUrl}/` ||
                        currentUrl.startsWith(`${excludedUrl}?`);
                    return isExactMatch;
                })
            ) {
                return false;
            }
        }

        if (
            this.targeting.excludedUrlContains &&
            this.targeting.excludedUrlContains.length > 0
        ) {
            if (
                this.targeting.excludedUrlContains.some((excludedPattern) => {
                    return currentUrl.includes(excludedPattern);
                })
            ) {
                return false;
            }
        }

        const passed = this.targeting.pages.some(
            (page) => page === '*' || currentPath.includes(page)
        );
        return passed;
    }    // Handle Escape key press
    handleEscapeKey(e) {
        if (e.key === 'Escape' && document.body.contains(this.popupWrapper)) {
            this.closePopup();
        }
    }// Create popup, overlay, styles, and close button
    createPopupElements() {
        // Create wrapper for Shadow DOM
        this.popupWrapper = document.createElement('div');
        this.popupWrapper.id = `popup-wrapper-${this.id}`;
        // Create shadow DOM
        this.shadowRoot = this.popupWrapper.attachShadow({ mode: 'closed' });

        // Create container
        this.popupContainer = document.createElement('div');
        this.popupContainer.id = `popup-${this.id}`;
        this.popupContainer.innerHTML = this.content;
        this.popupContainer.style.display = 'none';

        const positionStyles = this.getPositionStyles();
        const styleElem = document.createElement('style');
        styleElem.textContent = `
      :host {
        all: initial;
      }
      #popup-${this.id} {
        position: fixed;
        z-index: 99999999999;
        ${positionStyles}
        background-color: #fff;
        box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        border-radius: 5px;
        max-width: 90%;
        width: ${this.contentWidth}px;
        max-height: 90%;
        overflow: auto;
        will-change: transform, opacity;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        color: #333;
        line-height: 1.5;
        box-sizing: border-box;
        opacity: 1;
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        contain: layout style paint;
      }
      #popup-${this.id} * {
        box-sizing: border-box;
      }
      
      /* Animation Initial States */
      ${this.generateAnimationCSS()}
      
      /* Animation Final States */
      #popup-${this.id}.show {
        ${this.getAnimationTransform('', 1)};
      }
      .popup-overlay-${this.id} {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0,0,0,0.5);
        z-index: 99999999998;
        opacity: 0;
        transition: opacity 0.3s ease;
        will-change: opacity;
      }
      .popup-overlay-${this.id}.show {
        opacity: 1;
      }
      .popup-close-btn {
        position: absolute;
        top: 5px;
        right: 5px;
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #fff;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
      }
      .popup-close-btn:hover {
        color: #000;
        background-color: #f0f0f0;
        border-radius: 50%;
      }
      @media (max-width: 768px) {
        #popup-${this.id} {
          width: 90%;
          max-width: 90%;
        }
      }
    `;

        // Create overlay
        this.overlay = document.createElement('div');
        this.overlay.className = `popup-overlay-${this.id}`;
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.closePopup();
            }
        });

        // Create close button
        this.closeBtn = document.createElement('button');
        this.closeBtn.className = 'popup-close-btn';
        this.closeBtn.innerHTML = '×';
        this.closeBtn.setAttribute('aria-label', 'Close');
        this.closeBtn.addEventListener('click', () => this.closePopup());

        // Append close button
        this.popupContainer.appendChild(this.closeBtn);

        // Append all elements to shadow root
        this.shadowRoot.appendChild(styleElem);
        this.shadowRoot.appendChild(this.overlay);
        this.shadowRoot.appendChild(this.popupContainer);
    }    // Show popup
    showPopup() {
        // Add event listeners
        document.addEventListener('keydown', this.escapeKeyHandler);

        // Add wrapper to DOM (contains shadow root) - but keep hidden initially
        document.body.appendChild(this.popupWrapper);

        // Prerender the popup content and wait for it to be ready
        this.preRenderAndShow();

        // Update display count - always use localStorage for metrics
        const currentCount = parseInt(
            localStorage.getItem(this.displayCountKey) || '0',
            10
        );
        localStorage.setItem(
            this.displayCountKey,
            (currentCount + 1).toString()
        );

        const { frequency } = this.timing;

        if (frequency === 'once') {
            sessionStorage.setItem(this.popupKey, 'true');
        } else if (frequency === 'oncePerDay') {
            const today = new Date().toDateString();
            localStorage.setItem(this.popupKey, today);
        }
    }

    preRenderAndShow() {
        this.setPopupVisibility(false);

        // Wait for all content to load, then show with animation
        this.waitForAllContentLoaded()
            .then(() => this.showWithSmoothAnimation())
            .catch(() => this.showWithSmoothAnimation()); // Fallback if loading fails
    }

    setPopupVisibility(visible) {
        if (visible) {
            this.popupContainer.style.visibility = 'visible';
            this.popupContainer.style.opacity = '1';
        } else {
            this.popupContainer.style.display = 'block';
            this.popupContainer.style.visibility = 'hidden';
            this.popupContainer.style.opacity = '0';
        }
    }

    waitForAllContentLoaded() {
        return new Promise((resolve, reject) => {
            const images = this.popupContainer.querySelectorAll('img');

            if (images.length === 0) {
                resolve();
                return;
            }

            let loadedCount = 0;
            const totalImages = images.length;
            const timeout = setTimeout(() => reject(new Error('Timeout')), 3000);

            const handleImageComplete = () => {
                loadedCount++;
                if (loadedCount >= totalImages) {
                    clearTimeout(timeout);
                    resolve();
                }
            };

            images.forEach(img => {
                if (img.complete && img.naturalHeight !== 0) {
                    handleImageComplete();
                } else {
                    img.addEventListener('load', handleImageComplete, { once: true });
                    img.addEventListener('error', handleImageComplete, { once: true });
                }
            });
        });
    }

    showWithSmoothAnimation() {
        this.setPopupVisibility(true);
        this.popupContainer.classList.add(`popup-${this.animation}-initial`);

        this.popupContainer.offsetHeight;

        setTimeout(() => {
            this.overlay.classList.add('show');
            this.popupContainer.classList.remove(`popup-${this.animation}-initial`);
            this.popupContainer.classList.add('show');
        }, 10);
    }


    closePopup() {
        if (!this.popupContainer || !this.overlay) return;

        // Remove event listeners to prevent memory leaks
        document.removeEventListener('keydown', this.escapeKeyHandler);

        this.popupContainer.classList.remove('show');
        this.overlay.classList.remove('show');
        if (this.stopConditions.enableStopConditions !== false && this.stopConditions.afterClose) {
            const { frequency } = this.timing;

            if (frequency === 'once') {
                sessionStorage.setItem(this.popupKey, 'true');
            } else if (frequency === 'oncePerDay') {
                const today = new Date().toDateString();
                localStorage.setItem(this.popupKey, today);
            } else {
                localStorage.setItem(this.popupKey, 'true');
            }
        }

        setTimeout(() => {
            this.removeElements();
        }, 300);
    }

    // Remove elements from DOM
    removeElements() {
        if (document.body.contains(this.popupWrapper)) {
            document.body.removeChild(this.popupWrapper);
            // When removing the wrapper, all shadow DOM contents are automatically removed
        }
    }

    // Handle display triggers
    handleDisplayTrigger() {
        const delay = (this.timing.delay || 0) * 1000;

        if (this.displayTrigger === 'time') {
            setTimeout(() => this.showPopup(), delay);
        } else if (this.displayTrigger === 'scroll') {
            let scrollTriggered = false;
            const scrollHandler = () => {
                if (!scrollTriggered && window.scrollY > 100) {
                    scrollTriggered = true;
                    window.removeEventListener('scroll', scrollHandler);
                    setTimeout(() => this.showPopup(), delay);
                }
            };
            window.addEventListener('scroll', scrollHandler);
        } else if (this.displayTrigger === 'exit') {
            const exitHandler = (e) => {
                if (e.clientY <= 0) {
                    document.removeEventListener('mouseleave', exitHandler);
                    setTimeout(() => this.showPopup(), delay);
                }
            };
            document.addEventListener('mouseleave', exitHandler);
        }
    }

    getPositionStyles() {
        const positions = {
            'top-left': 'top: 20px; left: 20px;',
            'top-right': 'top: 20px; right: 20px;',
            'bottom-left': 'bottom: 20px; left: 20px;',
            'bottom-right': 'bottom: 20px; right: 20px;',
            'top-center': 'top: 20px; left: 50%; transform: translateX(-50%);',
            'top': 'top: 20px; left: 50%; transform: translateX(-50%);',
            'bottom-center': 'bottom: 20px; left: 50%; transform: translateX(-50%);',
            'bottom': 'bottom: 20px; left: 50%; transform: translateX(-50%);',
            'left': 'top: 50%; left: 20px; transform: translateY(-50%);',
            'right': 'top: 50%; right: 20px; transform: translateY(-50%);',
            'center': 'top: 50%; left: 50%; transform: translate(-50%, -50%);'
        };

        return positions[this.position] || positions.center;
    }

    generateAnimationCSS() {
        const ANIMATION_CONFIG = {
            fade: { transform: '', opacity: 0 },
            slide: { transform: 'translateX(100px)', opacity: 0 }, // Increased to 100px
            zoom: { transform: 'scale(0.7)', opacity: 0 },
            scale: { transform: 'scale(0.7)', opacity: 0 },
            bounce: { transform: 'scale(0.3)', opacity: 0 },
            'slide-up': { transform: 'translateY(100px)', opacity: 0 },
            'slide-down': { transform: 'translateY(-100px)', opacity: 0 },
            none: { transform: '', opacity: 1 }
        };

        return Object.entries(ANIMATION_CONFIG)
            .map(([name, config]) => `
                #popup-${this.id}.popup-${name}-initial {
                    ${this.getAnimationTransform(config.transform, config.opacity)};
                    transition: none !important;
                }`)
            .join('');
    }

    getAnimationTransform(animationTransform = '', opacity = 1) {
        const baseTransforms = {
            'top-left': animationTransform || 'none',
            'top-right': animationTransform || 'none',
            'bottom-left': animationTransform || 'none',
            'bottom-right': animationTransform || 'none',
            'top-center': animationTransform ? `translateX(-50%) ${animationTransform}` : 'translateX(-50%)',
            'top': animationTransform ? `translateX(-50%) ${animationTransform}` : 'translateX(-50%)',
            'bottom-center': animationTransform ? `translateX(-50%) ${animationTransform}` : 'translateX(-50%)',
            'bottom': animationTransform ? `translateX(-50%) ${animationTransform}` : 'translateX(-50%)',
            'left': animationTransform ? `translateY(-50%) ${animationTransform}` : 'translateY(-50%)',
            'right': animationTransform ? `translateY(-50%) ${animationTransform}` : 'translateY(-50%)',
            'center': animationTransform ? `translate(-50%, -50%) ${animationTransform}` : 'translate(-50%, -50%)'
        };

        const transform = baseTransforms[this.position] || baseTransforms.center;
        return `transform: ${transform}; opacity: ${opacity};`;
    }
}

export default PopupLibrary;
